import type { ReactNode } from 'react';

import { Button } from '@parrot-software/design-system';
import { ShieldCheck } from '@parrot-software/design-system/icons';

import { StepCard } from '@identity/presentation/components/base/StepCard';

export type SuccessCardProps = {
  /** Label of the exit button. Without it the card offers nothing to click. */
  actionLabel?: string;
  /** What the card says under the title, sometimes a `<Trans>` with a value in bold. */
  description: ReactNode;
  /** What leaving means for this flow. Each flow decides its own route and its own cleanup. */
  onAction?: () => void;
  title: string;
};

/**
 * The card every screen that ends well shares: the green shield, the header centered under it and,
 * when the flow has somewhere to go, a single exit button.
 *
 * The action is optional because the device that opens a magic link has nothing to continue: the
 * wizard is on the other one, so that screen confirms and stops there.
 *
 * The button spans the card on a phone, where a thumb aims at a row, and takes the width of its own
 * label from `sm` up, where a full-width button for two words reads as a banner.
 *
 * @param {SuccessCardProps} props - Component properties
 */
export const SuccessCard = ({ actionLabel, description, onAction, title }: SuccessCardProps) => (
  <StepCard
    align="center"
    description={description}
    icon={
      <span className="flex size-16 items-center justify-center rounded-full bg-bg-success">
        <ShieldCheck aria-hidden className="size-10 text-success" />
      </span>
    }
    title={title}
  >
    {actionLabel && onAction && (
      <div className="flex flex-col gap-6 font-sans sm:items-center">
        <Button className="w-full sm:w-fit" onClick={onAction} type="button">
          {actionLabel}
        </Button>
      </div>
    )}
  </StepCard>
);
